<template>
  <div>
    <!-- Success Rates, Failure Rates & Top 10 Warehouses Widgets -->
    <b-row>
      <b-col
        cols="12"
        xl="8"
      >
        <b-row>
          <!-- Success Rates -->
          <b-col
            cols="12"
            md="6"
          >
            <b-card class="h-90">
              <h5 class="metrics-heading">
                Success Rates
              </h5>
              <b-row class="justify-content-between metrics-row">
                <AppEchartPie
                  v-for="(month, index) in monthsData"
                  :key="'success-' + index"
                  :percentage="month.success_percentage"
                  :is-success="true"
                  :label="index"
                  :title="month.month"
                  :start-color="'#007AFF'"
                  :end-color="'#004999'"
                />
              </b-row>
            </b-card>
          </b-col>

          <!-- Failure Rates -->
          <b-col
            cols="12"
            md="6"
          >
            <b-card class="h-90">
              <h5 class="metrics-heading">
                Failure Rates
              </h5>
              <b-row class="justify-content-between metrics-row">
                <AppEchartPie
                  v-for="(month, index) in monthsData"
                  :key="'failure-' + index"
                  :percentage="month.unsuccess_percentage"
                  :is-success="false"
                  :label="index"
                  :title="month.month"
                  :start-color="'#FF0000'"
                  :end-color="'#990000'"
                  @onClickFailure="handleFailureChartClick"
                />
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </b-col>

      <!-- Top 10 Success Warehouses -->
      <b-col
        cols="12"
        xl="4"
      >
        <b-card class="h-90">
          <h5 class="warehouse-heading">
            Top 10 Success Warehouses
          </h5>
          <b-row
            v-if="warehouseData && warehouseData.length"
            class="metrics-row"
          >
            <b-col cols="6">
              <ul class="warehouse-list">
                <li
                  v-for="(warehouse, index) in warehouseData.slice(0, 5)"
                  :key="warehouse.warehouse_id"
                >
                  <span
                    class="rank"
                    :class="{ 'top-rank': index < 3 }"
                  >{{ index + 1 }}</span>
                  <span class="name dark-font">{{ warehouse.warehouse_name.replace('Branch',
                                                                                   '').trim() }}</span>
                </li>
              </ul>
            </b-col>
            <b-col cols="6">
              <ul class="warehouse-list">
                <li
                  v-for="(warehouse, index) in warehouseData.slice(5, 10)"
                  :key="warehouse.warehouse_id"
                >
                  <span
                    class="rank"
                    :class="{ 'top-rank': (index + 5) < 3 }"
                  >{{ index + 6 }}</span>
                  <span class="name dark-font">{{ warehouse.warehouse_name.replace('Branch',
                                                                                   '').trim() }}</span>
                </li>
              </ul>
            </b-col>
          </b-row>
          <b-row
            v-else
            class="metrics-row"
          >
            <b-col
              cols="12"
              class="no-data-row d-flex justify-content-center align-items-center text-center"
            >
              <span class="small no-data-text d-inline-block mb-1">
                No ranked warehouse data to show
              </span>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <div>
      <FilterCard :applied-filter-count="appliedFilterCount">

        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Merchant"
              label-for="merchant"
            >
              <v-select
                v-model="filters['merchant_id']"
                :options="merchantOptions"
                :reduce="option => option.id"
                label="name"
                placeholder="Select Merchant"
                @input="getMerchantBusinessList()"
                @clear="getMerchantBusinessList()"
              >
                <template v-slot:option="option">
                  {{ option.name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group
              label="Merchant Business"
              label-for="merchant_business"
            >
              <v-select
                id="merchant_business"
                v-model="filters['orderable_id']"
                :options="merchantBusinessOptions"
                :reduce="option => option.id"
                label="business_name"
                placeholder="Select Merchant Business"
              >
                <template
                  v-slot:option="option"
                  class="text-truncate"
                >
                  {{ option.business_name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group
              label="Status"
              label-for="status"
            >
              <v-select
                v-model="selectedStatusFilters"
                :options="statusOptions"
                :reduce="option => option.key"
                label="name"
                placeholder="Select Status"
                multiple
                deselect-from-dropdown
                class="status-dropdown"
              >
                <template v-slot:option="option">
                  {{ option.name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group
              label="Rider"
              label-for="rider"
            >
              <v-select
                v-model="filters['rider']"
                :options="riderOptions"
                :reduce="option => option.id"
                label="first_name"
                placeholder="Select Rider"
              >
                <template v-slot:option="option">
                  {{ option.first_name }} {{ option.last_name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Apply and Clear Filters -->
        <b-row class="mt-1">
          <b-col>
            <filter-buttons
              :filter-data="filters"
              :apply-filters="applyFilters"
              :clear-filters="clearFilters"
            />
          </b-col>
        </b-row>
      </FilterCard>
    </div>
    <div>
      <b-card no-body>
        <div class="m-2">
          <b-row>
            <b-col class="d-flex align-items-center justify-content-end mb-1 mb-md-0">
              <div>
                <ReportTableButtons
                  :items="items"
                  :json_fieldz="json_fields"
                  :name="`${downloadReportName} - Page ${page}.xls`"
                  :bulkname="`${downloadReportName}.xls`"
                  :fetch="fetchDeliveryProgressReportNoPagination"
                  @refresh="fetchDeliveryProgressReport"
                />
              </div>
            </b-col>
          </b-row>
        </div>
        <b-overlay
          id="overlay-background"
          blur="10px"
          opacity="0.30"
          rounded="sm"
          variant="light"
        >
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="items"
            :fields="fields"
            :json_fields="json_fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
          >
            <template #empty>
              <TableDataFetching
                :rows="rows"
                :data-loading="dataLoading"
              />
            </template>
            <template #cell(waybill_number)="data">
              <div class="d-flex align-items-center">
                <div
                  :class="{ 'font-weight-bolder ml-1 text-danger': data.item.temporary_warehouse_id != null, 'font-weight-bolder ml-1 text-primary': data.item.temporary_warehouse_id === null }"
                  @click="onClickViewOrder(data.item.id)"
                >
                  {{ data.value }}
                </div>
              </div>
            </template>
            <template #cell(order_current_rider)="data">
              <div class="d-flex align-items-center">
                <div>
                  <div class="font-weight-bolder">
                    {{ `${data.value ? data.value.riders.first_name : 'N/A'} ${data.value &&
                      data.value.riders.last_name ? data.value.riders.last_name : ''}` }}
                  </div>
                  <div class="font-small-2">
                    {{ data.value && data.value.riders.email ? data.value.riders.email : '' }}
                  </div>
                </div>
              </div>
            </template>
            <template #cell(latest_status_updated_at)="data">
              {{ __dateTimeFormatter(data.value) }}
            </template>
            <template #cell(customer_phone)="data">
              <div>{{ data.item.customer_phone }}</div>
            </template>
            <template #cell(order_current_status.key)="data">
              <order-status-badge :status-key="data.value" />
            </template>
          </b-table>
        </b-overlay>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              md="3"
              class="d-flex align-items-center justify-content-sm-start"
            >
              <label>Per page</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block ml-1 width-125"
              />
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              md="9"
              class="d-flex align-items-center justify-content-sm-end"
            >
              <span class="text-muted pagination-text  mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{
                meta.to }}
                of {{ meta.total }} entries</span>
              <b-pagination
                v-model="page"
                :total-rows="(meta.total) ? meta.total : 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>
    <view-order-modal ref="viewOrderModal" />
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BRow,
  BTable,
  VBModal,
  BOverlay,
  BPagination,
} from 'bootstrap-vue'
import AppEchartPie from '@core/components/charts/echart/AppEchartPie.vue'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import OrderStatusBadge from '@core/components/status/OrderStatusBadge.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ViewOrderModal from '@/components/AllOrders/ViewOrderModal.vue'
import FilterButtons from '@/components/Filter/ReportFilterButtons.vue'
import FilterCard from '@/components/FilterCard/FilterCard.vue'
import tableMixin from '@core/mixins/tableMixin'

const orderRepository = RepositoryFactory.get('order')
const riderRepository = RepositoryFactory.get('rider')
const ReportRepository = RepositoryFactory.get('report')
const merchantRepository = RepositoryFactory.get('merchant')

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    vSelect,
    BCard,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BOverlay,
    BPagination,
    FilterButtons,
    AppEchartPie,
    OrderStatusBadge,
    ViewOrderModal,
    FilterCard,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return {
      warehouse_id: null,
      month_key: 'current_month',
      filters: {},
      merchantOptions: [],
      selectedStatusFilters: [],
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 200],
      page: 1,
      meta: {},
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      fields: [
        {
          key: 'waybill_number',
          label: 'Waybill Number',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'merchant.name',
          label: 'Merchant',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'merchant_business.business_name',
          label: 'Merchant Business',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'customer_name',
          label: 'Customer Name',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'customer_address',
          label: 'Customer Address',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'customer_phone',
          label: 'Customer Phone',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'order_current_rider',
          label: 'Rider',
          sortable: true,
        },
        {
          key: 'order_current_status.key',
          label: 'Status',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: true,
        },
        {
          key: 'latest_status_updated_at',
          label: 'Status Changed Date',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
      ],
      json_fields: {
        'Waybill Number': 'waybill_number',
        Merchant: {
          field: 'merchant.name',
          callback: value => `"${value}"`,
        },
        'Merchant Business': {
          field: 'merchant_business.business_name',
          callback: value => `"${value}"`,
        },
        'Customer Name': {
          field: 'customer_name',
          callback: value => `"${value}"`,
        },
        'Customer Address': {
          field: 'customer_address',
          callback: value => `"${value}"`, // Wrap the address in double quotes
        },
        'Customer Phone': 'customer_phone',
        Rider: {
          callback: value => `"${value.order_current_rider ? `${value.order_current_rider.riders.first_name}` : 'N/A'} ${value.order_current_rider && value.order_current_rider.riders.last_name ? `${value.order_current_rider.riders.last_name}` : ''}"`,
        },
        Status: 'order_current_status.name',
        'Status Changed Date': {
          field: 'latest_status_updated_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
      },
      downloadReportName: 'Branch Delivery Progress',
      items: [],
      monthsData: {},
      warehouseData: [],
      merchantBusinessOptions: [],
      statusOptions: [],
      riderOptions: [],
    }
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchDeliveryProgressReport()
      },
    },
    deep: true,
    page() {
      this.fetchDeliveryProgressReport()
    },
    perPage() {
      this.fetchDeliveryProgressReport()
    },
    selectedStatusFilters: {
      async handler(val) {
        let keyList = ''
        val.forEach(key => {
          keyList += `${key},`
        })
        this.filters = {
          ...this.filters,
          status: keyList,
        }
      },
      deep: true,
    },
  },
  created() {
    this.warehouse_id = this.$route.params.id
    this.getDeliveryRate()
    this.getTopTenBranches()
  },
  async mounted() {
    await this.fetchDeliveryProgressReport()
    this.getMerchantList()
    this.fetchBusinessList()
    this.readOrderStatuses()
    this.getRiderList()
    this.updateFilterCount()
  },
  methods: {
    async getDeliveryRate() {
      this.monthsData = (await ReportRepository.getBranchDeliveryProgressSuccessAndFailure(this.warehouse_id)).data
    },
    async getTopTenBranches() {
      this.warehouseData = (await ReportRepository.getBranchDeliveryProgressTopTen()).data
    },
    async fetchDeliveryProgressReport() {
      this.dataLoading = true
      this.items = []
      try {
        const { data } = (await ReportRepository.getBranchDeliveryProgressReport(this.page, this.filterQuery, this.perPage, this.warehouse_id, this.month_key))
        this.items = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchDeliveryProgressReportNoPagination() {
      try {
        const { data } = (await ReportRepository.getBranchDeliveryProgressReportNoPagination(this.filterQuery, this.warehouse_id, this.month_key))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async getMerchantList() {
      this.merchantOptions = localStorage.getItem('merchant_list') ? JSON.parse(localStorage.getItem('merchant_list')) : (await merchantRepository.getMerchantListForDropdown()).data.data
      if (!localStorage.getItem('merchant_list')) localStorage.setItem('merchant_list', JSON.stringify(this.merchantOptions))
    },
    async fetchBusinessList() {
      this.merchantBusinessOptions = localStorage.getItem('business_list') ? JSON.parse(localStorage.getItem('business_list')) : (await merchantRepository.fetchBusinessList()).data.data
      if (!localStorage.getItem('business_list')) localStorage.setItem('business_list', JSON.stringify(this.merchantBusinessOptions))
    },
    async readOrderStatuses() {
      this.statusOptions = localStorage.getItem('order_statuses') ? JSON.parse(localStorage.getItem('order_statuses')) : (await orderRepository.getFullStatusList()).data.data
      if (!localStorage.getItem('order_statuses')) localStorage.setItem('order_statuses', JSON.stringify(this.statusOptions))
    },
    async getRiderList() {
      const res = await riderRepository.getRidersDropdownForAssignedWarehouse(this.warehouse_id)
      this.riderOptions = res.data?.data ?? []
    },
    async getMerchantBusinessList() {
      try {
        this.loading = true
        if (this.filters.merchant_id) {
          const { data } = (await merchantRepository
            .getMerchantBusinessListDropdown(this.filters.merchant_id)).data
          this.merchantBusinessOptions = data
          return
        }
        this.merchantBusinessOptions = localStorage.getItem('business_list')
          ? JSON.parse(localStorage.getItem('business_list'))
          : (await merchantRepository.fetchBusinessList()).data.data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    handleFailureChartClick(monthKey) {
      this.month_key = monthKey
      this.fetchDeliveryProgressReport()
    },
    onClickViewOrder(id) {
      this.$refs.viewOrderModal.openModal(id)
    },
    handleChangePage(page) {
      this.page = page
      this.fetchDeliveryProgressReport()
    },
    applyFilters() {
      this.fetchDeliveryProgressReport()
      this.updateFilterCount()
    },
    clearFilters() {
      this.selectedStatusFilters = ''
      this.filters = {}
      this.$nextTick(() => {
        this.fetchDeliveryProgressReport()
      })
      this.updateFilterCount()
    },
  },
}
</script>
<style lang="scss">
@import '../../../@core/scss/vue/libs/vue-select';

.warehouse-heading {
    font-weight: bold;
    color: #000;
}

.warehouse-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.warehouse-list li {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 6px;
    border-bottom: 1px solid #e0e0e0;
    font-size: 12px;
}

.warehouse-list li:last-child {
    border-bottom: none;
}

.rank {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-right: 5px;
    font-weight: bold;
    color: #333;
    border-radius: 50%;
    background: #f1f1f1;
    text-align: center;
}

.top-rank {
    background: #007bff;
    color: #fff;
}

.name {
    font-weight: 500;
    color: #333;
}

.metrics-heading {
    color: #000000;
    font-weight: bold;
}

.metrics-row {
    border-top: 1px solid #D9D9D9;
}

.h-90 {
    height: 90% !important;
}

.no-data-row {
    min-height: 150px;
}

.no-data-text {
    color: #000 !important;
}

@media (min-width: 767px) {
    .status-dropdown .vs__dropdown-menu {
        width: fit-content;
    }
}
</style>
